import React, { useState, useMemo } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, TextField, Button, MenuItem, 
    Select, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Chip, Divider, ToggleButton, ToggleButtonGroup, 
    Stack, IconButton, Tooltip, Collapse 
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function AccountingView() {
    const { orders, accountingLedger, cashTallies, addExpense, updateExpense, deleteExpense, saveCashTally } = useApp();
    const { currentLanguage } = useLanguage();

    const isMr = currentLanguage === 'mr';
    const isHi = currentLanguage === 'hi';

    // State for Collapsible Reconciliation Audit History (Default FALSE = Collapsed to fit on 1 screen)
    const [showAuditHistory, setShowAuditHistory] = useState(false);

    // Pure 100% i18n Dictionary (Zero mixed language strings)
    const lbl = {
        title: isMr ? 'हिशोब व गल्ला' : (isHi ? 'दैनिक खाता व नकद मिलान' : 'Daily Cash Tally & Expenses'),
        subtitle: isMr ? 'गल्ल्यातील प्रत्यक्ष रोख शिल्लक पडताळणी आणि दैनंदिन खर्चाचे वर्गीकरण' : (isHi ? 'दैनिक नकद मिलान और वर्गीकृत व्यय प्रबंधन' : 'Reconcile physical cash drawer and manage daily classified expenses'),

        kpiCashSales: isMr ? 'आजची रोख विक्री' : (isHi ? 'आज की नकद बिक्री' : 'Today Cash Sales'),
        kpiCounterExp: isMr ? 'गल्ला रोख खर्च' : (isHi ? 'गल्ला नकद खर्च' : 'Counter Cash Expense'),
        kpiExpectedCash: isMr ? 'अपेक्षित गल्ला शिल्लक' : (isHi ? 'अपेक्षित नकद शेष' : 'Expected Drawer Cash'),
        kpiTotalExp: isMr ? 'एकूण सर्व खर्च' : (isHi ? 'कुल सभी खर्च' : 'Total Expenses (All)'),

        secTallyTitle: isMr ? 'गल्ला ताळेबंद पडताळणी' : (isHi ? 'गल्ला ताळेबंद मिलान' : 'Daily Cash Drawer Tally'),
        badgeReconcile: isMr ? 'दैनंदिन जुळवणी' : (isHi ? 'दैनिक मिलान' : 'Daily Reconciliation'),
        lblOpeningCash: isMr ? 'शुरुवातीची गल्ला शिल्लक (रु.)' : (isHi ? 'आरंभिक नकद शेष (रु.)' : 'Opening Cash in Drawer (₹)'),
        lblActualCash: isMr ? 'प्रत्यक्ष मोजलेली रोख (रु.)' : (isHi ? 'वास्तविक गिनी गई नकद (रु.)' : 'Actual Closing Cash Counted (₹)'),
        phActualCash: isMr ? 'गल्ला मोजून रक्कम टाका' : (isHi ? 'नकद गिनकर राशि दर्ज करें' : 'Count physical cash & enter'),

        calcOpening: isMr ? 'शुरुवातीची शिल्लक:' : (isHi ? 'आरंभिक शेष:' : 'Opening Cash Balance:'),
        calcCashSales: isMr ? '(+) सॉफ्टवेअर रोख विक्री:' : (isHi ? '(+) सॉफ्टवेयर नकद बिक्री:' : '(+) System Cash Sales:'),
        calcCounterExp: isMr ? '(-) गल्ल्यातील रोख खर्च:' : (isHi ? '(-) गल्ला नकद खर्च:' : '(-) Counter Cash Expenses:'),
        calcExpected: isMr ? 'गल्ल्यात असायला हवी असलेली रक्कम:' : (isHi ? 'गल्ले में अपेक्षित राशि:' : 'Expected Drawer Closing Cash:'),

        statusMatch: isMr ? 'ताळेबंद १००% तंतोतंत जुळला!' : (isHi ? 'तालेबंद 100% सही मिला!' : 'Tally Matches Perfectly!'),
        statusSurplus: isMr ? 'गल्ल्यात जादा रक्कम आढळली' : (isHi ? 'गल्ले में अतिरिक्त राशि मिली' : 'Cash Surplus Found'),
        statusShortage: isMr ? 'गल्ल्यात तुट (फरक) आढळली' : (isHi ? 'गल्ले में कमी (अंतर) मिली' : 'Cash Shortage Found'),
        btnSaveTally: isMr ? '🖨️ गल्ला ताळेबंद सेव्ह करा' : (isHi ? '🖨️ तालेबंद सहेजें' : 'Lock & Save Daily Cash Tally'),

        secExpenseBookTitle: isMr ? 'आजचे खर्च रजिस्टर' : (isHi ? 'आज का खर्च रजिस्टर' : 'Daily Expense Book'),
        tabAll: isMr ? 'सर्व' : (isHi ? 'सभी' : 'All'),
        tabCash: isMr ? '💵 गल्ला रोख' : (isHi ? '💵 गल्ला नकद' : '💵 Counter Cash'),
        tabOnline: isMr ? '🏦 ऑनलाईन' : (isHi ? '🏦 ऑनलाइन' : '🏦 Online'),
        tabPersonal: isMr ? '👤 वैयक्तिक' : (isHi ? '👤 व्यक्तिगत' : '👤 Personal'),

        colItemTime: isMr ? 'खर्चाचा तपशील / वेळ' : (isHi ? 'विवरण / समय' : 'Item / Time'),
        colCategory: isMr ? 'कॅटेगिरी' : (isHi ? 'श्रेणी' : 'Category'),
        colSource: isMr ? 'माध्यम' : (isHi ? 'माध्यम' : 'Source'),
        colAmount: isMr ? 'रक्कम (रु.)' : (isHi ? 'राशि (रु.)' : 'Amount (₹)'),
        colAction: isMr ? 'कृती' : (isHi ? 'कार्रवाई' : 'Action'),
        noExpensesFound: isMr ? 'कोणताही खर्च नोंदवलेला नाही.' : (isHi ? 'कोई खर्च दर्ज नहीं हुआ।' : 'No expense records found.'),
        tooltipEdit: isMr ? 'दुरुस्त करा' : (isHi ? 'संशोधित करें' : 'Edit'),
        tooltipDelete: isMr ? 'हटवा' : (isHi ? 'हटाएं' : 'Delete'),

        // Form Section (Pure Language Dropdowns & Strings)
        formTitleAdd: isMr ? 'नवीन खर्चाची नोंद करा' : (isHi ? 'नए खर्च की प्रविष्टि करें' : 'Record Daily Expense'),
        formTitleEdit: isMr ? 'खर्चाची नोंद दुरुस्त करा' : (isHi ? 'खर्च प्रविष्टि संशोधित करें' : 'Edit Expense Record'),
        formSubAdd: isMr ? 'कॅटेगिरी व माहिती भरा' : (isHi ? 'श्रेणी व विवरण भरें' : 'Fill category & expense details'),
        formSubEdit: isMr ? 'बदल करून सेव्ह करा' : (isHi ? 'बदलाव कर सहेजें' : 'Update details & click save'),

        lblCat: isMr ? '१. खर्चाची कॅटेगिरी' : (isHi ? '1. खर्च की श्रेणी' : '1. Expense Category'),
        lblDesc: isMr ? '२. खर्चाचा तपशील / नाव' : (isHi ? '2. खर्च का विवरण / नाम' : '2. Expense Description / Item'),
        phDesc: isMr ? 'उदा. भाजीपाला खरेदी, गॅस रिफिल' : (isHi ? 'जैसे सब्जी खरीद, गैस रीफिल' : 'e.g. Vegetables, Gas Refill'),
        lblAmt: isMr ? '३. दिलेली रक्कम (रु.)' : (isHi ? '3. दी गई राशि (रु.)' : '3. Amount Paid (₹)'),
        lblSourceTitle: isMr ? '४. खर्च कशातून दिला?' : (isHi ? '4. खर्च किस माध्यम से दिया?' : '4. Paid From (Payment Source)'),

        srcCounterCashTitle: isMr ? '💵 काउंटर रोख' : (isHi ? '💵 काउंटर नकद' : '💵 Counter Cash'),
        srcCounterCashSub: isMr ? 'गल्ल्यातील रोख विक्रीतून वजा होईल' : (isHi ? 'गल्ले की नकद बिक्री से घटेगा' : 'Deducts from drawer cash'),

        srcOnlineTitle: isMr ? '🏦 ऑनलाईन / बँक खाते' : (isHi ? '🏦 ऑनलाइन / बैंक खाता' : '🏦 Online / Bank Account'),
        srcOnlineSub: isMr ? 'हॉटेल ऑनलाईन खात्यातून वजा होईल' : (isHi ? 'होटल के ऑनलाइन खाते से घटेगा' : 'Paid from hotel UPI/Bank'),

        srcPersonalTitle: isMr ? '👤 ओनर वैयक्तिक खाते' : (isHi ? '👤 मालिक का व्यक्तिगत खाता' : '👤 Personal Account'),
        srcPersonalSub: isMr ? 'ओनरच्या स्वतःच्या खिशातून दिलेला खर्च' : (isHi ? 'मालिक के स्वयं के जेब से दिया गया खर्च' : 'Paid from owner personal pocket'),

        btnSaveNew: isMr ? 'खर्चाची नोंद सेव्ह करा' : (isHi ? 'खर्च सहेजें' : 'Log Expense Record'),
        btnSaveEdit: isMr ? 'दुरुस्ती सेव्ह करा' : (isHi ? 'संशोधन सहेजें' : 'Save Changes'),
        btnCancel: isMr ? 'रद्द करा' : (isHi ? 'रद्द करें' : 'Cancel'),

        // Categories Dropdown pure items (Zero mixed language)
        catKitchen: isMr ? '🍲 स्वयंपाकघर कच्चा माल (भाजीपाला / किराणा)' : (isHi ? '🍲 रसोई कच्चा माल (सब्जी / किराना)' : '🍲 Kitchen Raw Ingredients'),
        catUtilities: isMr ? '⚡ युटिलिटीज (लाइट बिल, गॅस, पाणी)' : (isHi ? '⚡ उपयोगिताएं (बिजली बिल, गैस, पानी)' : '⚡ Utilities (Electricity, Gas, Water)'),
        catWages: isMr ? '👨‍🍳 दैनिक स्टाफ पगार व ॲडव्हान्स' : (isHi ? '👨‍🍳 दैनिक स्टाफ वेतन व अग्रिम' : '👨‍🍳 Daily Staff Wages & Advances'),
        catMaintenance: isMr ? '🛠️ देखभाल व दुरुस्ती' : (isHi ? '🛠️ रखरखाव व मरम्मत' : '🛠️ Maintenance & Repairs'),
        catRent: isMr ? '🏢 जागा भाडे व कर' : (isHi ? '🏢 स्थान किराया व कर' : '🏢 Rent & Taxes'),
        catOthers: isMr ? '📋 इतर किरकोळ खर्च' : (isHi ? '📋 अन्य विविध खर्च' : '📋 Others & Miscellaneous'),

        // Audit log section
        secAuditTitle: isMr ? 'मागील गल्ला ताळेबंद इतिहास' : (isHi ? 'पिछले तालेबंद का इतिहास' : 'Reconciliation Audit History'),
        auditColDate: isMr ? 'तारीख' : (isHi ? 'तारीख' : 'Date'),
        auditColExpected: isMr ? 'अपेक्षित' : (isHi ? 'अपेक्षित' : 'Expected'),
        auditColActual: isMr ? 'प्रत्यक्ष' : (isHi ? 'वास्तविक' : 'Actual'),
        auditColDiff: isMr ? 'फरक' : (isHi ? 'अंतर' : 'Diff'),
        noTalliesLogged: isMr ? 'अद्याप ताळेबंद नोंदी उपलब्ध नाहीत.' : (isHi ? 'कोई तालेबंद रिकॉर्ड उपलब्ध नहीं है।' : 'No tallies logged yet.')
    };

    // Edit Mode State (null if adding new, or expense object if editing)
    const [editingExpenseId, setEditingExpenseId] = useState(null);

    // Expense Form States (Category FIRST as requested)
    const [expenseCategory, setExpenseCategory] = useState('Kitchen Raw');
    const [expenseDesc, setExpenseDesc] = useState('');
    const [expenseAmount, setExpenseAmount] = useState('');
    const [paymentSource, setPaymentSource] = useState('counter_cash');

    // Filter tab for Expense Book (all | counter_cash | online | personal)
    const [expenseFilter, setExpenseFilter] = useState('all');

    // Tally states
    const [openingCash, setOpeningCash] = useState('2000');
    const [actualClosingCash, setActualClosingCash] = useState('');

    // Calculate Cash Sales today
    const todayCashSales = useMemo(() => {
        return orders
            .filter(o => o.status === 'served')
            .reduce((sum, o) => {
                const method = (o.paymentMethod || '').toLowerCase();
                if (method === 'cash') return sum + (Number(o.total) || 0);
                if (method.includes('split')) {
                    const match = method.match(/cash:\s*₹?(\d+(\.\d+)?)/i);
                    if (match) return sum + parseFloat(match[1]);
                }
                return sum;
            }, 0);
    }, [orders]);

    // Calculate Aggregator Sales today
    const todayZomatoSales = useMemo(() => {
        return orders
            .filter(o => o.status === 'served')
            .reduce((sum, o) => {
                const method = (o.paymentMethod || '').toLowerCase();
                return method === 'zomato' ? sum + (Number(o.total) || 0) : sum;
            }, 0);
    }, [orders]);

    const todaySwiggySales = useMemo(() => {
        return orders
            .filter(o => o.status === 'served')
            .reduce((sum, o) => {
                const method = (o.paymentMethod || '').toLowerCase();
                return method === 'swiggy' ? sum + (Number(o.total) || 0) : sum;
            }, 0);
    }, [orders]);

    // Counter Cash expenses today
    const counterCashExpensesToday = useMemo(() => {
        return accountingLedger
            .filter(e => (e.paymentSource || 'counter_cash') === 'counter_cash')
            .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    }, [accountingLedger]);

    // Online & Personal Expenses today
    const onlineExpensesToday = useMemo(() => {
        return accountingLedger
            .filter(e => e.paymentSource === 'online')
            .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    }, [accountingLedger]);

    const personalExpensesToday = useMemo(() => {
        return accountingLedger
            .filter(e => e.paymentSource === 'personal')
            .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    }, [accountingLedger]);

    const totalExpensesToday = useMemo(() => {
        return accountingLedger.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    }, [accountingLedger]);

    // Tally Calculation
    const expectedClosingCash = (parseFloat(openingCash) || 0) + todayCashSales - counterCashExpensesToday;
    const actualClosingCashVal = parseFloat(actualClosingCash) || 0;
    const variance = actualClosingCash ? (actualClosingCashVal - expectedClosingCash) : 0;

    // Filtered Expenses
    const filteredExpenses = useMemo(() => {
        if (expenseFilter === 'all') return accountingLedger;
        return accountingLedger.filter(e => (e.paymentSource || 'counter_cash') === expenseFilter);
    }, [accountingLedger, expenseFilter]);

    // Handle Form Submit (Add OR Update)
    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!expenseDesc || !expenseAmount) return;

        if (editingExpenseId) {
            updateExpense(editingExpenseId, {
                desc: expenseDesc,
                amount: parseFloat(expenseAmount) || 0,
                category: expenseCategory,
                paymentSource: paymentSource
            });
            handleCancelEdit();
        } else {
            addExpense(expenseDesc, expenseAmount, expenseCategory, paymentSource);
            setExpenseDesc('');
            setExpenseAmount('');
        }
    };

    // Load Expense into Form for Edit
    const handleStartEdit = (exp) => {
        setEditingExpenseId(exp.id);
        setExpenseCategory(exp.category || 'Kitchen Raw');
        setExpenseDesc(exp.desc || '');
        setExpenseAmount(String(exp.amount || ''));
        setPaymentSource(exp.paymentSource || 'counter_cash');
    };

    // Cancel Edit Mode
    const handleCancelEdit = () => {
        setEditingExpenseId(null);
        setExpenseDesc('');
        setExpenseAmount('');
        setExpenseCategory('Kitchen Raw');
        setPaymentSource('counter_cash');
    };

    // Delete Expense with confirmation
    const handleDelete = (expId) => {
        if (window.confirm(isMr ? 'खात्री आहे? हा खर्चाचा दाखला हटवायचा आहे का?' : (isHi ? 'क्या आप निश्चित हैं? इस खर्च को हटाना चाहते हैं?' : 'Are you sure you want to delete this expense record?'))) {
            deleteExpense(expId);
            if (editingExpenseId === expId) handleCancelEdit();
        }
    };

    const handleSaveTally = () => {
        if (!actualClosingCash) return;
        saveCashTally(openingCash, actualClosingCash, todayCashSales, counterCashExpensesToday);
        setActualClosingCash('');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            {/* Header Title Banner */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalanceWalletIcon sx={{ color: '#008751', fontSize: 34 }} />
                        {lbl.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mt: 0.5 }}>
                        {lbl.subtitle}
                    </Typography>
                </Box>
            </Box>

            {/* Quick KPI Stat Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PointOfSaleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                                {lbl.kpiCashSales}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#16a34a', my: 0.5 }}>
                                ₹{todayCashSales.toFixed(0)}
                            </Typography>
                            <Chip label="Counter Cash" size="small" sx={{ fontWeight: 800, bgcolor: '#f0fdf4', color: '#15803d', height: 18, fontSize: '0.65rem' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocalAtmIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                {lbl.kpiCounterExp}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#dc2626', my: 0.5 }}>
                                -₹{counterCashExpensesToday.toFixed(0)}
                            </Typography>
                            <Chip label="Drawer Outflow" size="small" sx={{ fontWeight: 800, bgcolor: '#fef2f2', color: '#b91c1c', height: 18, fontSize: '0.65rem' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#008751' }} />
                                {lbl.kpiExpectedCash}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#008751', my: 0.5 }}>
                                ₹{expectedClosingCash.toFixed(0)}
                            </Typography>
                            <Chip label="Drawer Balance" size="small" sx={{ fontWeight: 800, bgcolor: '#f0fdf4', color: '#166534', height: 18, fontSize: '0.65rem' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ReceiptLongIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                                {lbl.kpiTotalExp}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#ea580c', my: 0.5 }}>
                                -₹{totalExpensesToday.toFixed(0)}
                            </Typography>
                            <Chip 
                                label={`Online: ₹${onlineExpensesToday} | Personal: ₹${personalExpensesToday}`} 
                                size="small" 
                                sx={{ fontWeight: 800, bgcolor: '#fff7ed', color: '#c2410c', height: 18, fontSize: '0.65rem' }} 
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Bulletproof Side-by-Side Flex Layout (Left Tables 62%, Right Sticky Form 38%) */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
                
                {/* LEFT CONTAINER (62% width on desktop): Cash Drawer Tally Board & Expense Book */}
                <Box sx={{ width: { xs: '100%', md: '62%' }, flexShrink: 0 }}>
                    {/* Section 1: Cash Drawer Tally Board */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', mb: 2.5 }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.05rem' }}>
                                    🗄️ {lbl.secTallyTitle}
                                </Typography>
                                <Chip 
                                    label={lbl.badgeReconcile} 
                                    size="small" 
                                    sx={{ fontWeight: 800, bgcolor: '#f0fdf4', color: '#166534', height: 22, fontSize: '0.7rem' }} 
                                />
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={lbl.lblOpeningCash}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={openingCash}
                                        onChange={(e) => setOpeningCash(e.target.value)}
                                        sx={{ '& fieldset': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={lbl.lblActualCash}
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={actualClosingCash}
                                        onChange={(e) => setActualClosingCash(e.target.value)}
                                        placeholder={lbl.phActualCash}
                                        sx={{ '& fieldset': { borderRadius: 2, borderColor: '#008751' } }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Summary Calculation Board */}
                            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                                <Grid container spacing={1}>
                                    <Grid item xs={7}>
                                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.82rem' }}>
                                            {lbl.calcOpening}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right">
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#334155' }}>
                                            ₹{(parseFloat(openingCash) || 0).toFixed(2)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={7}>
                                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.82rem' }}>
                                            {lbl.calcCashSales}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right">
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                                            +₹{todayCashSales.toFixed(2)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={7}>
                                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.82rem' }}>
                                            {lbl.calcCounterExp}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right">
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#dc2626' }}>
                                            -₹{counterCashExpensesToday.toFixed(2)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                                    <Grid item xs={7}>
                                        <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 800 }}>
                                            {lbl.calcExpected}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} textAlign="right">
                                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#008751' }}>
                                            ₹{expectedClosingCash.toFixed(2)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Discrepancy Status Bar */}
                            {actualClosingCash && (
                                <Box sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: variance === 0 ? '#f0fdf4' : (variance > 0 ? '#eff6ff' : '#fef2f2'),
                                    border: `1px solid ${variance === 0 ? '#bbf7d0' : (variance > 0 ? '#bfdbfe' : '#fecaca')}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {variance === 0 ? (
                                            <CheckCircleIcon sx={{ color: '#16a34a' }} />
                                        ) : (
                                            <ErrorIcon sx={{ color: variance > 0 ? '#2563eb' : '#dc2626' }} />
                                        )}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: variance === 0 ? '#15803d' : (variance > 0 ? '#1e40af' : '#b91c1c') }}>
                                            {variance === 0 ? lbl.statusMatch : (variance > 0 ? lbl.statusSurplus : lbl.statusShortage)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: variance === 0 ? '#15803d' : (variance > 0 ? '#1e40af' : '#b91c1c') }}>
                                        {variance > 0 ? `+₹${variance.toFixed(2)}` : `₹${variance.toFixed(2)}`}
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                fullWidth
                                disabled={!actualClosingCash}
                                onClick={handleSaveTally}
                                sx={{ mt: 2, py: 1, fontWeight: 900, textTransform: 'none', borderRadius: 2.5, bgcolor: '#008751', '&:hover': { bgcolor: '#006f42' } }}
                            >
                                {lbl.btnSaveTally}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Section: Accounts Receivables (Aggregators) */}
                    {(todayZomatoSales > 0 || todaySwiggySales > 0) && (
                        <Card sx={{ borderRadius: 3, border: '1px solid #fed7aa', boxShadow: 'none', mb: 2.5, bgcolor: '#fff7ed' }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#9a3412', display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.05rem', mb: 2 }}>
                                    ⏳ Accounts Receivables (येणे बाकी)
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #fdba74' }}>
                                            <Typography variant="caption" sx={{ color: '#cb202d', fontWeight: 800 }}>Zomato</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1e293b' }}>₹{todayZomatoSales.toFixed(2)}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #fdba74' }}>
                                            <Typography variant="caption" sx={{ color: '#fc8019', fontWeight: 800 }}>Swiggy</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1e293b' }}>₹{todaySwiggySales.toFixed(2)}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Section 2: Daily Outflow Expense Book List */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', mb: 2.5 }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.05rem' }}>
                                    📖 {lbl.secExpenseBookTitle}
                                </Typography>

                                {/* Payment Source Filter Toggle */}
                                <ToggleButtonGroup
                                    value={expenseFilter}
                                    exclusive
                                    onChange={(e, val) => val && setExpenseFilter(val)}
                                    size="small"
                                    sx={{ bgcolor: '#f1f5f9', borderRadius: 2 }}
                                >
                                    <ToggleButton value="all" sx={{ fontWeight: 800, px: 1.2, py: 0.3, textTransform: 'none', fontSize: '0.75rem' }}>
                                        {lbl.tabAll}
                                    </ToggleButton>
                                    <ToggleButton value="counter_cash" sx={{ fontWeight: 800, px: 1.2, py: 0.3, textTransform: 'none', fontSize: '0.75rem' }}>
                                        {lbl.tabCash}
                                    </ToggleButton>
                                    <ToggleButton value="online" sx={{ fontWeight: 800, px: 1.2, py: 0.3, textTransform: 'none', fontSize: '0.75rem' }}>
                                        {lbl.tabOnline}
                                    </ToggleButton>
                                    <ToggleButton value="personal" sx={{ fontWeight: 800, px: 1.2, py: 0.3, textTransform: 'none', fontSize: '0.75rem' }}>
                                        {lbl.tabPersonal}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colItemTime}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colCategory}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colSource}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colAmount}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 900, color: '#1e293b', width: 75 }}>{lbl.colAction}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredExpenses.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                    {lbl.noExpensesFound}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredExpenses.map((exp) => {
                                                const src = exp.paymentSource || 'counter_cash';
                                                const isCash = src === 'counter_cash';
                                                const isOnline = src === 'online';

                                                return (
                                                    <TableRow key={exp.id} hover sx={{ bgcolor: editingExpenseId === exp.id ? '#fff7ed' : 'inherit' }}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>{exp.desc}</Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                {exp.date ? new Date(exp.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={exp.category || 'General'}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={isCash ? lbl.srcCounterCashTitle : (isOnline ? lbl.srcOnlineTitle : lbl.srcPersonalTitle)}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    fontSize: '0.65rem',
                                                                    height: 20,
                                                                    bgcolor: isCash ? '#fee2e2' : (isOnline ? '#e0f2fe' : '#fef3c7'),
                                                                    color: isCash ? '#991b1b' : (isOnline ? '#075985' : '#92400e')
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#dc2626', fontSize: '0.9rem' }}>
                                                            -₹{Number(exp.amount).toFixed(2)}
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap', p: 0.5 }}>
                                                            <Tooltip title={lbl.tooltipEdit}>
                                                                <IconButton size="small" color="primary" onClick={() => handleStartEdit(exp)}>
                                                                    <EditIcon sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={lbl.tooltipDelete}>
                                                                <IconButton size="small" color="error" onClick={() => handleDelete(exp.id)}>
                                                                    <DeleteIcon sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    {/* Section 3: Collapsible Reconciliation Audit History (Compact Single-Screen View) */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent 
                            onClick={() => setShowAuditHistory(!showAuditHistory)}
                            sx={{ 
                                p: 2, 
                                cursor: 'pointer', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                '&:last-child': { pb: showAuditHistory ? 1.5 : 2 }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DateRangeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                {lbl.secAuditTitle}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                    label={`${cashTallies.length} ${isMr ? 'नोंदी' : 'Logs'}`} 
                                    size="small" 
                                    sx={{ fontWeight: 800, height: 20, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#475569' }} 
                                />
                                {showAuditHistory ? <ExpandLessIcon sx={{ color: '#64748b' }} /> : <ExpandMoreIcon sx={{ color: '#64748b' }} />}
                            </Box>
                        </CardContent>

                        <Collapse in={showAuditHistory} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, pb: 2 }}>
                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }}>{lbl.auditColDate}</TableCell>
                                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }} align="right">{lbl.auditColExpected}</TableCell>
                                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }} align="right">{lbl.auditColActual}</TableCell>
                                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }} align="right">{lbl.auditColDiff}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cashTallies.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 1.5, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                        {lbl.noTalliesLogged}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                cashTallies.slice(0, 5).map((log) => (
                                                    <TableRow key={log.id}>
                                                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                                                            {new Date(log.date).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>₹{log.expectedClosing}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 800 }}>₹{log.actualClosing}</TableCell>
                                                        <TableCell align="right">
                                                            <Chip
                                                                label={log.variance === 0 ? '0' : (log.variance > 0 ? `+₹${log.variance}` : `-₹${Math.abs(log.variance)}`)}
                                                                size="small"
                                                                color={log.variance === 0 ? 'success' : 'error'}
                                                                sx={{ fontWeight: 900, fontSize: '0.65rem', height: 18 }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Collapse>
                    </Card>
                </Box>

                {/* RIGHT STICKY CONTAINER (38% width): STARTS AT VERY TOP, CONTAINS ONLY RECORD EXPENSE FORM */}
                <Box sx={{ width: { xs: '100%', md: '38%' }, position: { md: 'sticky' }, top: { md: 20 }, flexShrink: 0 }}>
                    <Card sx={{ borderRadius: 3, border: editingExpenseId ? '2px solid #0284c7' : '2px solid #ea580c', boxShadow: '0 4px 14px rgba(234, 88, 12, 0.12)', bgcolor: '#ffffff' }}>
                        <Box sx={{ p: 2, bgcolor: editingExpenseId ? '#0284c7' : '#ea580c', color: '#ffffff', borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {editingExpenseId ? <EditIcon /> : <AddCircleIcon />}
                                    {editingExpenseId ? lbl.formTitleEdit : lbl.formTitleAdd}
                                </Typography>
                                {editingExpenseId && (
                                    <IconButton size="small" onClick={handleCancelEdit} sx={{ color: '#ffffff' }}>
                                        <CancelIcon />
                                    </IconButton>
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: editingExpenseId ? '#e0f2fe' : '#ffedd5', display: 'block', mt: 0.3 }}>
                                {editingExpenseId ? lbl.formSubEdit : lbl.formSubAdd}
                            </Typography>
                        </Box>

                        <CardContent sx={{ p: 2.5 }}>
                            <form onSubmit={handleFormSubmit}>
                                <Stack spacing={2}>
                                    {/* 1. EXPENSE CATEGORY FIRST (Pure Marathi/Hindi/English - Zero mixed text) */}
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel sx={{ fontWeight: 800 }}>{lbl.lblCat}</InputLabel>
                                        <Select
                                            value={expenseCategory}
                                            label={lbl.lblCat}
                                            onChange={(e) => setExpenseCategory(e.target.value)}
                                            sx={{ borderRadius: 2, fontWeight: 800 }}
                                        >
                                            <MenuItem value="Kitchen Raw">{lbl.catKitchen}</MenuItem>
                                            <MenuItem value="Utilities">{lbl.catUtilities}</MenuItem>
                                            <MenuItem value="Wages">{lbl.catWages}</MenuItem>
                                            <MenuItem value="Maintenance">{lbl.catMaintenance}</MenuItem>
                                            <MenuItem value="Rent">{lbl.catRent}</MenuItem>
                                            <MenuItem value="Others">{lbl.catOthers}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {/* 2. EXPENSE DESCRIPTION */}
                                    <TextField
                                        label={lbl.lblDesc}
                                        fullWidth
                                        required
                                        size="small"
                                        value={expenseDesc}
                                        onChange={(e) => setExpenseDesc(e.target.value)}
                                        placeholder={lbl.phDesc}
                                        sx={{ '& fieldset': { borderRadius: 2 } }}
                                    />

                                    {/* 3. AMOUNT PAID */}
                                    <TextField
                                        label={lbl.lblAmt}
                                        type="number"
                                        fullWidth
                                        required
                                        size="small"
                                        value={expenseAmount}
                                        onChange={(e) => setExpenseAmount(e.target.value)}
                                        sx={{ '& fieldset': { borderRadius: 2 } }}
                                    />

                                    {/* 4. VISUAL PAYMENT SOURCE SELECTOR */}
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', mb: 1, display: 'block' }}>
                                            {lbl.lblSourceTitle}
                                        </Typography>

                                        <Stack spacing={1}>
                                            <Paper
                                                variant="outlined"
                                                onClick={() => setPaymentSource('counter_cash')}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.2,
                                                    bgcolor: paymentSource === 'counter_cash' ? '#fef2f2' : '#ffffff',
                                                    borderColor: paymentSource === 'counter_cash' ? '#dc2626' : '#cbd5e1',
                                                    borderWidth: paymentSource === 'counter_cash' ? 2 : 1
                                                }}
                                            >
                                                <PaymentsIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#991b1b', lineHeight: 1.2, fontSize: '0.85rem' }}>
                                                        {lbl.srcCounterCashTitle}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#7f1d1d', fontSize: '0.68rem' }}>
                                                        {lbl.srcCounterCashSub}
                                                    </Typography>
                                                </Box>
                                            </Paper>

                                            <Paper
                                                variant="outlined"
                                                onClick={() => setPaymentSource('online')}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.2,
                                                    bgcolor: paymentSource === 'online' ? '#e0f2fe' : '#ffffff',
                                                    borderColor: paymentSource === 'online' ? '#0284c7' : '#cbd5e1',
                                                    borderWidth: paymentSource === 'online' ? 2 : 1
                                                }}
                                            >
                                                <AccountBalanceIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#075985', lineHeight: 1.2, fontSize: '0.85rem' }}>
                                                        {lbl.srcOnlineTitle}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#0c4a6e', fontSize: '0.68rem' }}>
                                                        {lbl.srcOnlineSub}
                                                    </Typography>
                                                </Box>
                                            </Paper>

                                            <Paper
                                                variant="outlined"
                                                onClick={() => setPaymentSource('personal')}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.2,
                                                    bgcolor: paymentSource === 'personal' ? '#fef3c7' : '#ffffff',
                                                    borderColor: paymentSource === 'personal' ? '#d97706' : '#cbd5e1',
                                                    borderWidth: paymentSource === 'personal' ? 2 : 1
                                                }}
                                            >
                                                <PersonIcon sx={{ color: '#d97706', fontSize: 20 }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#92400e', lineHeight: 1.2, fontSize: '0.85rem' }}>
                                                        {lbl.srcPersonalTitle}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.68rem' }}>
                                                        {lbl.srcPersonalSub}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Stack>
                                    </Box>

                                    {/* Submit / Update Buttons */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {editingExpenseId && (
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                fullWidth
                                                onClick={handleCancelEdit}
                                                sx={{ fontWeight: 800, borderRadius: 2 }}
                                            >
                                                {lbl.btnCancel}
                                            </Button>
                                        )}
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            startIcon={editingExpenseId ? <SaveIcon /> : <AddCircleIcon />}
                                            sx={{ 
                                                py: 1.2, 
                                                fontWeight: 900, 
                                                textTransform: 'none', 
                                                borderRadius: 2.5, 
                                                bgcolor: editingExpenseId ? '#0284c7' : '#ea580c', 
                                                '&:hover': { bgcolor: editingExpenseId ? '#0369a1' : '#c2410c' } 
                                            }}
                                        >
                                            {editingExpenseId ? lbl.btnSaveEdit : lbl.btnSaveNew}
                                        </Button>
                                    </Box>
                                </Stack>
                            </form>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}
